import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  GAME_META,
  ROUND_LABEL,
  getMatches,
  updateMatch,
  getAllStreaming,
  upsertStreaming,
} from "@/lib/api";
import Footer from "@/components/Footer";
import {
  ChevronDown,
  Save,
  Radio,
  CircleDot,
} from "lucide-react";

function MatchEditor({ match, onSave }) {
  const [team1, setTeam1] = useState(match.team1 || "");
  const [team2, setTeam2] = useState(match.team2 || "");
  const [score1, setScore1] = useState(match.score1 ?? "");
  const [score2, setScore2] = useState(match.score2 ?? "");
  const [status, setStatus] = useState(match.status || "scheduled");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        team1: team1 || null,
        team2: team2 || null,
        score1: score1 === "" ? null : Number(score1),
        score2: score2 === "" ? null : Number(score2),
        status,
      };
      const updated = await updateMatch(match.id, payload);
      onSave(updated);
      toast.success("Partida atualizada");
    } catch (e) {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`match-edit-${match.id}`}
      className="border border-white/10 bg-[#141414] p-4 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_auto] items-center gap-3"
    >
      <div className="flex items-center gap-2">
        <input
          data-testid={`team1-${match.id}`}
          value={team1}
          onChange={(e) => setTeam1(e.target.value)}
          placeholder="Equipa 1"
          className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#00F0FF] outline-none"
        />
        <input
          data-testid={`score1-${match.id}`}
          value={score1}
          onChange={(e) => setScore1(e.target.value)}
          type="number"
          placeholder="—"
          className="w-14 bg-black border border-white/10 px-2 py-2 text-sm text-center focus:border-[#00F0FF] outline-none"
        />
      </div>
      <span className="text-white/40 text-xs uppercase tracking-wider text-center">
        vs
      </span>
      <div className="flex items-center gap-2">
        <input
          data-testid={`score2-${match.id}`}
          value={score2}
          onChange={(e) => setScore2(e.target.value)}
          type="number"
          placeholder="—"
          className="w-14 bg-black border border-white/10 px-2 py-2 text-sm text-center focus:border-[#00F0FF] outline-none"
        />
        <input
          data-testid={`team2-${match.id}`}
          value={team2}
          onChange={(e) => setTeam2(e.target.value)}
          placeholder="Equipa 2"
          className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#00F0FF] outline-none"
        />
      </div>
      <select
        data-testid={`status-${match.id}`}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        className="bg-black border border-white/10 px-2 py-2 text-xs uppercase tracking-wider focus:border-[#00F0FF] outline-none"
      >
        <option value="scheduled">Agendada</option>
        <option value="live">Ao Vivo</option>
        <option value="finished">Terminada</option>
      </select>
      <button
        data-testid={`save-${match.id}`}
        onClick={submit}
        disabled={saving}
        className="bg-[#00F0FF] text-black px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white disabled:opacity-50 transition-colors flex items-center gap-2"
      >
        <Save className="h-3.5 w-3.5" />
        {saving ? "..." : "Guardar"}
      </button>
    </div>
  );
}

function StreamingEditor({ game, streaming, onSave }) {
  const [url, setUrl] = useState(streaming?.url || "");
  const [platform, setPlatform] = useState(streaming?.platform || "YouTube");
  const [isLive, setIsLive] = useState(!!streaming?.is_live);
  const [saving, setSaving] = useState(false);

  const meta = GAME_META[game];

  const submit = async () => {
    setSaving(true);
    try {
      const updated = await upsertStreaming(game, {
        url,
        platform,
        is_live: isLive,
      });
      onSave(updated);
      toast.success(`Streaming de ${meta.short} atualizado`);
    } catch {
      toast.error("Erro ao guardar streaming");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`streaming-edit-${game}`}
      className="border border-white/10 bg-[#141414] p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio
            className="h-4 w-4"
            style={{ color: meta.accent }}
            strokeWidth={2}
          />
          <span className="font-display text-lg uppercase tracking-wider">
            {meta.name}
          </span>
        </div>
        {isLive && (
          <span className="text-[10px] uppercase tracking-wider text-[#FF3B30] pulse-live">
            ● AO VIVO
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-[1fr_140px] gap-3">
        <input
          data-testid={`stream-url-${game}`}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://youtube.com/..."
          className="bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#00F0FF] outline-none"
        />
        <select
          data-testid={`stream-platform-${game}`}
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className="bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#00F0FF] outline-none"
        >
          <option>YouTube</option>
          <option>Twitch</option>
          <option>Instagram</option>
          <option>TikTok</option>
        </select>
      </div>

      <div className="flex items-center justify-between mt-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            data-testid={`stream-live-${game}`}
            type="checkbox"
            checked={isLive}
            onChange={(e) => setIsLive(e.target.checked)}
            className="accent-[#00F0FF] h-4 w-4"
          />
          <span className="text-xs uppercase tracking-wider text-white/70">
            Está ao vivo agora
          </span>
        </label>
        <button
          data-testid={`stream-save-${game}`}
          onClick={submit}
          disabled={saving}
          className="bg-[#00F0FF] text-black px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? "..." : "Guardar"}
        </button>
      </div>
    </div>
  );
}

function GameAdminBlock({ gameKey, defaultOpen = false }) {
  const meta = GAME_META[gameKey];
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    getMatches(gameKey)
      .then(setMatches)
      .finally(() => setLoading(false));
  }, [gameKey]);

  const byRound = useMemo(() => {
    const order = ["R32", "R16", "QF", "SF", "F"];
    const grouped = {};
    for (const m of matches) {
      if (!grouped[m.round]) grouped[m.round] = [];
      grouped[m.round].push(m);
    }
    for (const r of Object.keys(grouped)) {
      grouped[r].sort((a, b) => a.slot - b.slot);
    }
    return order.filter((r) => grouped[r]).map((r) => [r, grouped[r]]);
  }, [matches]);

  const onSave = (updated) =>
    setMatches((arr) => arr.map((m) => (m.id === updated.id ? updated : m)));

  return (
    <div className="border border-white/10 bg-[#0A0A0A]">
      <button
        data-testid={`toggle-${gameKey}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <CircleDot
            className="h-4 w-4"
            style={{ color: meta.accent }}
          />
          <span className="font-display text-2xl uppercase tracking-wider">
            {meta.name}
          </span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
            {matches.length} partidas
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-white/5 p-5 space-y-6">
          {loading ? (
            <div className="text-white/50 text-sm uppercase tracking-wider">
              A carregar...
            </div>
          ) : (
            byRound.map(([round, list]) => (
              <div key={round}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#00F0FF]">
                    {ROUND_LABEL[round]}
                  </span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>
                <div className="space-y-2">
                  {list.map((m) => (
                    <MatchEditor key={m.id} match={m} onSave={onSave} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const [streamings, setStreamings] = useState({});
  const [tab, setTab] = useState("results");

  useEffect(() => {
    if (user && user.role === "admin") {
      getAllStreaming().then((list) => {
        const map = {};
        for (const s of list) map[s.game] = s;
        setStreamings(map);
      });
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white/50 text-sm uppercase tracking-wider">
        A verificar sessão...
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  const updateStream = (game, s) =>
    setStreamings((prev) => ({ ...prev, [game]: s }));

  return (
    <div data-testid="admin-dashboard" className="bg-grain min-h-screen">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
              Painel de Administração
            </div>
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wider mt-1">
              Comando Central
            </h1>
            <p className="text-sm text-white/60 mt-3">
              Atualiza resultados, brackets e links de streaming.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-white/40">
            Sessão: <span className="text-[#00F0FF]">{user.email}</span>
          </div>
        </div>

        <div className="flex gap-1 mb-8 border-b border-white/10">
          {[
            { k: "results", l: "Resultados & Bracket" },
            { k: "streaming", l: "Streaming" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.k
                  ? "border-[#00F0FF] text-[#00F0FF]"
                  : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "results" && (
          <div className="space-y-4">
            {["fc25", "fortnite", "freefire", "codmobile"].map((g) => (
              <GameAdminBlock key={g} gameKey={g} defaultOpen={g === "fc25"} />
            ))}
          </div>
        )}

        {tab === "streaming" && (
          <div className="grid md:grid-cols-2 gap-4">
            {["fc25", "fortnite", "freefire", "codmobile"].map((g) => (
              <StreamingEditor
                key={g}
                game={g}
                streaming={streamings[g]}
                onSave={(s) => updateStream(g, s)}
              />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
